import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '@environments/environment';
import { map, Observable } from 'rxjs';
import { DisplayLoad, Load, ShipmentsResponse } from '@services/shipment.model';
import { ShipmentTrackingCheckpoint } from '@tiltgroup/web-components';

@Injectable({
  providedIn: 'root',
})
export class TrackerService {
  private http = inject(HttpClient);

  getShipments(
    trackingNumbers: string[],
    customerRef?: string
  ): Observable<DisplayLoad[]> {
    const endpoint$ = customerRef
      ? this.http.post<ShipmentsResponse>(
          `${environment.apiURL}/tracking/ref`,
          {
            reference_numbers: trackingNumbers,
            client_id: customerRef,
          }
        )
      : this.http.post<ShipmentsResponse>(
          `${environment.apiURL}/tracking/pro`,
          {
            pro_numbers: trackingNumbers,
          }
        );

    return endpoint$.pipe(
      map(
        (response) =>
          response.loads?.map((load) => ({
            ...load,
            statusCheckpoints: this.buildStatusCheckpoints(load),
            displayStatus: this.buildStatus(load),
          })) || []
      )
    );
  }

  buildStatusCheckpoints(shipment: Load): ShipmentTrackingCheckpoint[] {
    const checkpoints = [];

    const baseCheckpoint = {
      completed: false,
      icon: 'lt:ic-incomplete',
    };

    const booked = {
      ...baseCheckpoint,
      status: 'Booked',
      icon: shipment.last_update_at ? 'lt:ic-completed' : 'lt:ic-incomplete',
      completed: !!shipment.last_update_at,
    };

    const pickedUp = {
      ...baseCheckpoint,
      status: 'Picked up',
      icon: shipment.actual_pickup ? 'lt:ic-completed' : 'lt:ic-incomplete',
      completed: !!shipment.actual_pickup,
    };

    const inTransit = {
      ...baseCheckpoint,
      status: 'In transit',
      icon: shipment.estimated_delivery
        ? 'lt:ic-completed'
        : 'lt:ic-incomplete',
      completed: shipment.estimated_delivery || shipment.actual_delivery,
    };

    const delivered = {
      ...baseCheckpoint,
      status: 'Delivered',
      icon: shipment.actual_delivery ? 'lt:ic-completed' : 'lt:ic-incomplete',
      completed: !!shipment.actual_delivery,
    };

    checkpoints.push(booked, pickedUp, inTransit, delivered);

    return checkpoints;
  }

  buildStatus(load: Load): string {
    if (load.actual_delivery) {
      return 'Delivered';
    } else if (load.estimated_delivery && !load.actual_delivery) {
      return 'In transit';
    } else if (load.actual_pickup) {
      return 'Picked up';
    } else if (load.last_update_at) {
      return 'Booked';
    } else {
      return 'Unknown';
    }
  }
}
